import styled from "styled-components";
import { lg, sm } from "../../../BreakPoints";

export const Page = styled.main`
  min-height: 100vh;
  padding: 3.9rem 1.2rem 1.6rem;
  background: radial-gradient(circle at 10% 1%, rgba(59, 99, 247, 0.16), transparent 28%),
    radial-gradient(circle at 90% 16%, rgba(46, 196, 182, 0.14), transparent 28%), var(--ice-blue);
`;

export const Content = styled.section`
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  gap: 1rem;
`;

export const Header = styled.header`
  border-radius: 0.7rem;
  padding: 0.9rem 1rem;
  background: linear-gradient(132deg, var(--deep-navy), var(--deep-navy-soft));
  color: var(--surface-white);
`;

export const HeaderTitle = styled.h1`
  font-size: clamp(1.55rem, 3.5vw, 2.2rem);
`;

export const HeaderText = styled.p`
  margin-top: 0.5rem;
  color: rgba(244, 248, 255, 0.92);
  font-size: 0.94rem;
  line-height: 1.6;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 1rem;

  ${lg({
    gridTemplateColumns: "1fr",
  })}
`;

export const FormCard = styled.article`
  border-radius: 0.7rem;
  padding: 1rem;
  background: var(--surface-white);
  border: 1px solid var(--line-mid);
  box-shadow: 0 8px 16px rgba(19, 57, 133, 0.08);
`;

export const Form = styled.form`
  display: grid;
  gap: 0.7rem;
`;

export const FormTitle = styled.h2`
  font-size: 1.1rem;
  color: var(--deep-navy);
`;

export const FormText = styled.p`
  margin-top: 0.1rem;
  color: var(--text-muted);
  font-size: 0.83rem;
  line-height: 1.55;
`;

export const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.6rem;

  ${sm({
    gridTemplateColumns: "1fr",
  })}
`;

export const Input = styled.input`
  width: 100%;
  border: 1px solid var(--line-mid);
  border-radius: 0.62rem;
  padding: 0.7rem 0.82rem;
  outline: none;
  font-size: 0.9rem;
  background: rgba(255, 255, 255, 0.95);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: var(--slate);
  }

  &:focus {
    border-color: var(--brand-blue);
    box-shadow: 0 0 0 4px rgba(59, 99, 247, 0.12);
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  border: 1px solid var(--line-mid);
  border-radius: 0.62rem;
  padding: 0.7rem 0.82rem;
  outline: none;
  resize: vertical;
  font-size: 0.9rem;
  font-family: var(--font-body);
  background: rgba(255, 255, 255, 0.95);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: var(--slate);
  }

  &:focus {
    border-color: var(--brand-blue);
    box-shadow: 0 0 0 4px rgba(59, 99, 247, 0.12);
  }
`;

export const RatingRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
`;

export const StarButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font-size: 1.28rem;
  line-height: 1;
  color: ${(props) => (props.active ? "var(--coral)" : "var(--line-mid)")};
  transition: transform 0.15s ease, color 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    color: var(--coral);
  }
`;

export const RatingText = styled.span`
  font-size: 0.84rem;
  color: var(--text-muted);
`;

export const SubmitButton = styled.button`
  border: none;
  border-radius: 0.72rem;
  padding: 0.75rem 1rem;
  background: var(--brand-blue);
  color: var(--surface-white);
  font-family: var(--font-display);
  font-size: 0.92rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
  box-shadow: 0 14px 24px rgba(18, 72, 179, 0.25);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 18px 28px rgba(18, 72, 179, 0.32);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

export const ReviewPanel = styled.article`
  border-radius: 0.7rem;
  padding: 1rem;
  background: var(--surface-white);
  border: 1px solid var(--line-mid);
  box-shadow: 0 8px 16px rgba(19, 57, 133, 0.08);
`;

export const ReviewTitle = styled.h2`
  font-size: 1.12rem;
  color: var(--deep-navy);
`;

export const Empty = styled.p`
  margin-top: 0.7rem;
  color: var(--text-muted);
  font-size: 0.9rem;
`;

export const ReviewList = styled.div`
  margin-top: 0.8rem;
  display: grid;
  gap: 0.65rem;
  max-height: 560px;
  overflow-y: auto;
  padding-right: 0.2rem;
`;

export const ReviewCard = styled.article`
  border-radius: 0.86rem;
  border: 1px solid rgba(22, 71, 171, 0.14);
  background: var(--surface-white);
  padding: 0.85rem;
  display: grid;
  gap: 0.42rem;
`;

export const ReviewHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

export const Reviewer = styled.h3`
  font-size: 0.95rem;
  color: var(--deep-navy);
`;

export const DateText = styled.p`
  font-size: 0.75rem;
  color: var(--slate);
`;

export const ReviewContent = styled.p`
  color: var(--text-muted);
  font-size: 0.87rem;
  line-height: 1.55;
`;

export const Stars = styled.p`
  font-size: 0.92rem;
  color: var(--coral);
`;
